// src/components/ImageUploader.jsx
import { useState, useRef, useCallback } from "react";
import { uploadImage, getOptimizedUrl } from "../lib/cloudinary";

export default function ImageUploader({ images, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef();

  const handleFiles = useCallback(async (files) => {
    const validFiles = Array.from(files).filter(f => f.type.startsWith("image/")).slice(0, 6 - images.length);
    if (!validFiles.length) return;

    setUploading(true);
    const newUrls = [];

    for (const file of validFiles) {
      try {
        const url = await uploadImage(file, (pct) => {
          setProgress(p => ({ ...p, [file.name]: pct }));
        });
        newUrls.push(url);
      } catch (e) {
        console.error("Upload error:", e);
      }
    }

    onChange([...images, ...newUrls]);
    setUploading(false);
    setProgress({});
  }, [images, onChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const removeImage = (idx) => {
    onChange(images.filter((_, i) => i !== idx));
  };

  const isConfigured = !!process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;

  return (
    <div className="image-uploader">
      {images.length > 0 && (
        <div className="image-grid">
          {images.map((url, i) => (
            <div key={i} className="image-thumb">
              <img src={getOptimizedUrl(url, 300)} alt={`Item ${i + 1}`} />
              <button className="image-remove" onClick={() => removeImage(i)} title="Remove">×</button>
              {i === 0 && <span className="image-main-badge">Main</span>}
            </div>
          ))}
        </div>
      )}

      {images.length < 6 && (
        <div
          className={`drop-zone ${dragOver ? "drag-over" : ""} ${uploading ? "uploading" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => isConfigured && inputRef.current?.click()}
          style={{ cursor: isConfigured ? "pointer" : "default" }}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={e => handleFiles(e.target.files)}
          />
          {!isConfigured ? (
            <div className="drop-zone-inner">
              <div className="drop-icon">☁</div>
              <div className="drop-text">Add <code>REACT_APP_CLOUDINARY_CLOUD_NAME</code> to enable photo uploads</div>
            </div>
          ) : uploading ? (
            <div className="drop-zone-inner">
              <div className="upload-progress-list">
                {Object.entries(progress).map(([name, pct]) => (
                  <div key={name} className="upload-progress-row">
                    <span className="upload-filename">{name.length > 20 ? name.slice(0, 20) + "…" : name}</span>
                    <div className="upload-bar"><div style={{ width: `${pct}%` }} /></div>
                    <span className="upload-pct">{pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="drop-zone-inner">
              <div className="drop-icon">↑</div>
              <div className="drop-label">Drop photos here or tap to browse</div>
              <div className="drop-sub">Up to {6 - images.length} more · JPG, PNG, HEIC</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
